import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Filter,
  Search,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  Eye,
  Send,
  Inbox,
  User,
  ChevronRight,
  XCircle,
  Loader2,
} from "lucide-react";
import apiService from "../../services/api";
import { toast } from "react-hot-toast";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import CreateTeamTaskModal from "../../components/CreateTeamTaskModal";
import { useTheme } from "../../contexts/ThemeContext";

const TeamTasks = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState("assigned"); // "assigned" | "created"
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    type: "",
    unreadOnly: false,
  });
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    pending: 0,
    completed: 0,
  });
  const [createdStats, setCreatedStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
  });

  useEffect(() => {
    loadTasks();
  }, [filters, activeTab]);

  useEffect(() => {
    loadStats();
  }, []);

  // Real-time sync for task updates
  useRealtimeSync({
    events: ["team_task_notification"],
    onUpdate: (data) => {
      if (
        data.action === "new_task_assigned" ||
        data.action === "task_comment_added" ||
        data.action === "assignment_accepted" ||
        data.action === "assignment_declined" ||
        data.action === "assignment_completed"
      ) {
        loadTasks();
        loadStats();
      }
    },
    enabled: true,
  });

  const loadTasks = async () => {
    try {
      setLoading(true);
      if (activeTab === "assigned") {
        const response = await apiService.getMyTeamTasks(filters);
        setTasks(response.data || []);
      } else {
        const response = await apiService.getCreatedTeamTasks({
          status: filters.status,
          priority: filters.priority,
          type: filters.type,
        });
        setTasks(response.data || []);
      }
    } catch (error) {
      console.error("Error loading team tasks:", error);
      toast.error("Failed to load team tasks");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiService.getTeamTaskStats();
      const data = response.data || {};
      setStats(data);

      try {
        const createdResponse = await apiService.getCreatedTeamTasks({});
        const createdTasks = createdResponse.data || [];
        const cStats = {
          total: createdTasks.length,
          pending: 0,
          inProgress: 0,
          completed: 0,
        };
        createdTasks.forEach((task) => {
          if (task.status === "pending") cStats.pending++;
          else if (task.status === "in_progress") cStats.inProgress++;
          else if (task.status === "completed") cStats.completed++;
        });
        setCreatedStats(cStats);
      } catch (e) {
        console.error("Error loading created stats:", e);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleMarkAsRead = async (taskId) => {
    try {
      await apiService.markTeamTaskAsRead(taskId);
      loadTasks();
      loadStats();
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleTaskCreated = (response) => {
    setShowCreateModal(false);
    toast.success(response.message || "Team task created successfully!");
    loadTasks();
    loadStats();
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: isDarkMode
        ? "text-gray-400 bg-gray-400/10"
        : "text-gray-500 bg-gray-100",
      medium: isDarkMode
        ? "text-yellow-400 bg-yellow-400/10"
        : "text-yellow-600 bg-yellow-50",
      high: isDarkMode
        ? "text-orange-400 bg-orange-400/10"
        : "text-orange-600 bg-orange-50",
      urgent: isDarkMode
        ? "text-red-400 bg-red-400/10 animate-pulse"
        : "text-red-600 bg-red-50 animate-pulse",
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-600 text-white",
      accepted: "bg-blue-500 text-white",
      in_progress: "bg-blue-600 text-white",
      completed: "bg-green-600 text-white",
      cancelled: "bg-gray-600 text-white",
      on_hold: "bg-orange-600 text-white",
      declined: "bg-red-600 text-white",
    };
    return colors[status] || colors.pending;
  };

  const getAssignmentStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={14} className="text-green-400" />;
      case "accepted":
      case "in_progress":
        return <Loader2 size={14} className="text-blue-400" />;
      case "declined":
        return <XCircle size={14} className="text-red-400" />;
      default:
        return <Clock size={14} className="text-yellow-400" />;
    }
  };

  const currentStats = activeTab === "assigned" ? stats : createdStats;

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div
      className={`rounded-xl p-6 border transition-colors ${
        isDarkMode
          ? "bg-[#0C0E12] border-gray-700/50 hover:border-gray-600/50"
          : "bg-white border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon size={20} />
        </div>
      </div>
      <div
        className={`text-2xl font-bold mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        {value}
      </div>
      <div
        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        {title}
      </div>
    </div>
  );

  // Card for tasks assigned to me
  const AssignedTaskCard = ({ task }) => {
    const isUnread = !task.myAssignment?.notificationRead;

    return (
      <div
        className={`rounded-xl border transition-all cursor-pointer ${
          isDarkMode
            ? `bg-[#0C0E12] hover:border-[#427BCA]/50 ${
                isUnread
                  ? "border-[#427BCA]/30 shadow-lg shadow-[#427BCA]/10"
                  : "border-gray-700/50"
              }`
            : `bg-white hover:border-[#427BCA]/50 ${
                isUnread
                  ? "border-[#427BCA]/40 shadow-md shadow-[#427BCA]/10"
                  : "border-gray-200"
              }`
        }`}
        onClick={() => navigate(`/team-tasks/${task.id}`)}
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                {isUnread && (
                  <div className="w-2 h-2 bg-[#427BCA] rounded-full animate-pulse flex-shrink-0" />
                )}
                <h3
                  className={`font-semibold text-lg truncate ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {task.title}
                </h3>
              </div>
              <p
                className={`text-sm line-clamp-2 mb-3 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {task.description}
              </p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                  task.priority,
                )}`}
              >
                {task.priority?.toUpperCase()}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
            <div
              className={`flex items-center gap-2 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <Users size={16} />
              <span>From: {task.creator?.name}</span>
            </div>
            {task.dueDate && (
              <div
                className={`flex items-center gap-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <Clock size={16} />
                <span>{new Date(task.dueDate).toLocaleDateString()}</span>
              </div>
            )}
            <div>
              <span
                className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                  task.myAssignment?.status || "pending",
                )}`}
              >
                {(task.myAssignment?.status || "pending")
                  .replace("_", " ")
                  .toUpperCase()}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {task.assignments?.length > 1 && (
                <div
                  className={`flex items-center gap-1 text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  <Users size={14} />
                  <span>
                    {task.assignments.length} assignee
                    {task.assignments.length > 1 ? "s" : ""}
                  </span>
                </div>
              )}
              {task.comments?.length > 0 && (
                <div
                  className={`flex items-center gap-1 text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  <AlertCircle size={14} />
                  <span>{task.comments.length} comments</span>
                </div>
              )}
            </div>
            {isUnread && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAsRead(task.id);
                }}
                className="text-xs text-[#427BCA] hover:text-[#5A9BE8] font-medium"
              >
                Mark as Read
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Card for tasks I created / assigned to others
  const CreatedTaskCard = ({ task }) => {
    const assignees = task.assignments || [];
    const completedCount = assignees.filter(
      (a) => a.status === "completed",
    ).length;
    const declinedCount = assignees.filter(
      (a) => a.status === "declined",
    ).length;

    return (
      <div
        className={`rounded-xl border transition-all cursor-pointer hover:border-[#427BCA]/50 ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700/50"
            : "bg-white border-gray-200"
        }`}
        onClick={() => navigate(`/team-tasks/${task.id}`)}
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <h3
                className={`font-semibold text-lg truncate mb-2 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {task.title}
              </h3>
              <p
                className={`text-sm line-clamp-2 mb-3 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {task.description}
              </p>
            </div>
            <div className="ml-4 flex-shrink-0 flex flex-col items-end gap-2">
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                  task.priority,
                )}`}
              >
                {task.priority?.toUpperCase()}
              </span>
              <span
                className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                  task.status,
                )}`}
              >
                {task.status?.replace("_", " ").toUpperCase()}
              </span>
            </div>
          </div>

          {/* Assignee status summary */}
          <div className="mb-4">
            <div
              className={`text-xs mb-2 font-medium uppercase tracking-wide ${
                isDarkMode ? "text-gray-500" : "text-gray-400"
              }`}
            >
              Assignees ({completedCount}/{assignees.length} completed
              {declinedCount > 0 ? `, ${declinedCount} declined` : ""})
            </div>
            <div className="flex flex-wrap gap-2">
              {assignees.map((assignment) => (
                <div
                  key={assignment.id}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                    isDarkMode ? "bg-[#13161B]" : "bg-gray-100"
                  }`}
                >
                  {assignment.user?.profileImageUrl ? (
                    <img
                      src={assignment.user.profileImageUrl}
                      alt={assignment.user.name}
                      className="w-5 h-5 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        isDarkMode ? "bg-gray-600" : "bg-gray-300"
                      }`}
                    >
                      <User
                        size={12}
                        className={
                          isDarkMode ? "text-gray-300" : "text-gray-600"
                        }
                      />
                    </div>
                  )}
                  <span
                    className={`text-xs ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {assignment.user?.name}
                  </span>
                  {getAssignmentStatusIcon(assignment.status)}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm">
            {task.dueDate && (
              <div
                className={`flex items-center gap-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <Clock size={16} />
                <span>{new Date(task.dueDate).toLocaleDateString()}</span>
              </div>
            )}
            {task.team && (
              <div
                className={`flex items-center gap-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <Users size={16} />
                <span>{task.team.name}</span>
              </div>
            )}
            {task.comments?.length > 0 && (
              <div
                className={`flex items-center gap-1 text-xs ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                <AlertCircle size={14} />
                <span>{task.comments.length} comments</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`min-h-screen p-6 ${isDarkMode ? "bg-[#0A0B0D]" : "bg-gray-50"}`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1
              className={`text-3xl font-bold mb-2 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Team Tasks
            </h1>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Manage and track your team assignments
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#427BCA] hover:bg-[#5A9BE8] text-white rounded-lg font-medium transition-colors"
          >
            <Plus size={20} />
            Create Task
          </button>
        </div>

        {/* Tabs */}
        <div
          className={`flex items-center gap-1 mb-6 rounded-xl border p-1 w-fit ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700/50"
              : "bg-white border-gray-200"
          }`}
        >
          <button
            onClick={() => {
              setActiveTab("assigned");
              setFilters({
                status: "",
                priority: "",
                type: "",
                unreadOnly: false,
              });
            }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "assigned"
                ? "bg-[#427BCA] text-white"
                : isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-white/5"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            <Inbox size={18} />
            Assigned to Me
            {stats.pending > 0 && activeTab !== "assigned" && (
              <span className="ml-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold px-2 py-0.5 rounded-full">
                {stats.pending}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setActiveTab("created");
              setFilters({
                status: "",
                priority: "",
                type: "",
                unreadOnly: false,
              });
            }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "created"
                ? "bg-[#427BCA] text-white"
                : isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-white/5"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            <Send size={18} />
            Created by Me
            {createdStats.total > 0 && activeTab !== "created" && (
              <span className="ml-1 bg-blue-500/20 text-blue-400 text-xs font-bold px-2 py-0.5 rounded-full">
                {createdStats.total}
              </span>
            )}
          </button>
        </div>

        {/* Stats */}
        {activeTab === "assigned" ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Total Assigned"
              value={stats.total || 0}
              icon={Inbox}
              color="bg-blue-500/20 text-blue-400"
            />
            <StatCard
              title="Unread"
              value={stats.unread || 0}
              icon={Eye}
              color="bg-[#427BCA]/20 text-[#427BCA]"
            />
            <StatCard
              title="Pending"
              value={stats.pending || 0}
              icon={Clock}
              color="bg-yellow-500/20 text-yellow-400"
            />
            <StatCard
              title="Completed"
              value={stats.completed || 0}
              icon={CheckCircle}
              color="bg-green-500/20 text-green-400"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Total Created"
              value={createdStats.total}
              icon={Send}
              color="bg-blue-500/20 text-blue-400"
            />
            <StatCard
              title="Pending"
              value={createdStats.pending}
              icon={Clock}
              color="bg-yellow-500/20 text-yellow-400"
            />
            <StatCard
              title="In Progress"
              value={createdStats.inProgress}
              icon={Loader2}
              color="bg-blue-500/20 text-blue-400"
            />
            <StatCard
              title="Completed"
              value={createdStats.completed}
              icon={CheckCircle}
              color="bg-green-500/20 text-green-400"
            />
          </div>
        )}

        {/* Filters */}
        <div
          className={`rounded-xl border p-4 mb-6 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700/50"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter
                size={18}
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
              />
              <span
                className={`text-sm font-medium ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Filters:
              </span>
            </div>

            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-[#427BCA] ${
                isDarkMode
                  ? "bg-[#13161B] border-gray-700 text-white"
                  : "bg-gray-50 border-gray-300 text-gray-900"
              }`}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              {activeTab === "assigned" && (
                <option value="accepted">Accepted</option>
              )}
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              {activeTab === "created" && (
                <>
                  <option value="cancelled">Cancelled</option>
                  <option value="on_hold">On Hold</option>
                </>
              )}
            </select>

            <select
              value={filters.priority}
              onChange={(e) =>
                setFilters({ ...filters, priority: e.target.value })
              }
              className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-[#427BCA] ${
                isDarkMode
                  ? "bg-[#13161B] border-gray-700 text-white"
                  : "bg-gray-50 border-gray-300 text-gray-900"
              }`}
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>

            {activeTab === "assigned" && (
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={filters.unreadOnly}
                  onChange={(e) =>
                    setFilters({ ...filters, unreadOnly: e.target.checked })
                  }
                  className={`w-4 h-4 rounded border-gray-300 text-[#427BCA] focus:ring-[#427BCA] ${
                    isDarkMode ? "border-gray-700 bg-[#13161B]" : ""
                  }`}
                />
                Unread Only
              </label>
            )}
          </div>
        </div>

        {/* Tasks List */}
        <div className="space-y-4">
          {loading ? (
            <div
              className={`text-center py-12 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <Loader2
                size={24}
                className={`animate-spin mx-auto mb-3 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              />
              Loading tasks...
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12">
              {activeTab === "assigned" ? (
                <>
                  <Inbox
                    size={48}
                    className={`mx-auto mb-4 ${
                      isDarkMode ? "text-gray-600" : "text-gray-300"
                    }`}
                  />
                  <p
                    className={`mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    No tasks assigned to you
                  </p>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-600" : "text-gray-400"
                    }`}
                  >
                    Tasks assigned to you by team members will appear here
                  </p>
                </>
              ) : (
                <>
                  <Send
                    size={48}
                    className={`mx-auto mb-4 ${
                      isDarkMode ? "text-gray-600" : "text-gray-300"
                    }`}
                  />
                  <p
                    className={`mb-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    You haven't created any tasks yet
                  </p>
                  <p
                    className={`text-sm ${
                      isDarkMode ? "text-gray-600" : "text-gray-400"
                    }`}
                  >
                    Create a task and assign it to your team members
                  </p>
                </>
              )}
            </div>
          ) : (
            tasks.map((task) =>
              activeTab === "assigned" ? (
                <AssignedTaskCard key={task.id} task={task} />
              ) : (
                <CreatedTaskCard key={task.id} task={task} />
              ),
            )
          )}
        </div>
      </div>

      {/* Create Team Task Modal */}
      {showCreateModal && (
        <CreateTeamTaskModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleTaskCreated}
        />
      )}
    </div>
  );
};

export default TeamTasks;
